import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import './SlashCommandList.css';

/* The "/" menu popup. Keyboard contract matches MentionList (up/down/enter),
   so both suggestion popups in the app behave identically. */
const SlashCommandList = forwardRef((props, ref) => {
	const [selectedIndex, setSelectedIndex] = useState(0);
	const listRef = useRef(null);

	const selectItem = (index) => {
		const item = props.items[index];
		if (item) props.command(item);
	};

	useEffect(() => setSelectedIndex(0), [props.items]);

	/* The list scrolls, so arrowing past the visible window must bring the
	   selection with it — otherwise the highlight moves invisibly off-screen and
	   Enter fires on an item the user can't see. `block: 'nearest'` scrolls only
	   when the item is actually out of view, so it doesn't jitter on every keypress. */
	useEffect(() => {
		listRef.current?.children[selectedIndex]?.scrollIntoView({ block: 'nearest' });
	}, [selectedIndex]);

	useImperativeHandle(ref, () => ({
		onKeyDown: ({ event }) => {
			if (event.key === 'ArrowUp') {
				setSelectedIndex((i) => (i + props.items.length - 1) % props.items.length);
				return true;
			}
			if (event.key === 'ArrowDown') {
				setSelectedIndex((i) => (i + 1) % props.items.length);
				return true;
			}
			if (event.key === 'Enter') {
				selectItem(selectedIndex);
				return true;
			}
			return false;
		},
	}));

	if (props.items.length === 0) {
		return (
			<div className='slash-list'>
				<div className='slash-empty'>No blocks match</div>
			</div>
		);
	}

	return (
		<div className='slash-list' ref={listRef}>
			{props.items.map((item, index) => (
				<button
					type='button'
					key={item.title}
					className={`slash-item ${index === selectedIndex ? 'is-selected' : ''}`}
					onMouseEnter={() => setSelectedIndex(index)}
					onClick={() => selectItem(index)}>
					<span className='slash-item__icon' aria-hidden='true'>
						<i className={`fa-solid fa-fw ${item.icon}`}></i>
					</span>
					<span className='slash-item__text'>
						<span className='slash-item__title'>{item.title}</span>
						<span className='slash-item__hint'>{item.hint}</span>
					</span>
				</button>
			))}
		</div>
	);
});

SlashCommandList.displayName = 'SlashCommandList';

export default SlashCommandList;
